import { useCallbackRef } from "../lifecycle";
import {
  paymentTypes,
  updateCheckoutState,
} from "src/utils/redux/slices/checkout";
import {
  createAddress,
  updateBillingUserRequest,
} from "src/utils/api/purchase/ordersCheckoutApi";
import { useAppDispatch, useCheckout } from "../redux";
import { setBillingUser } from "src/utils/redux/slices/checkoutOrders";
import {
  errorConstants,
  ValidationError,
} from "src/utils/services/PurchaseErrorMessages";
import { captureException } from "src/utils/error";
import i18n from "src/locales";
import getStore from "src/utils/redux/store";
import { getActiveUser, useAuth } from "@noom/hive-auth";
import { requiresPhysicalAddress } from "./physical-address";
import {
  isSelectedMedPlanWithMedication,
  shouldUseStripePaymentElement,
} from "src/utils/userSegment/features";
import { getFullNameFromBillingAddress } from "src/utils/name";
import { selectResolvedPhysicalAddressShortRegion } from "src/utils/redux/slices/checkoutPhysicalAddress/address-selectors";
import {
  PaymentMethodProto,
  PaymentMethodProto_Source,
} from "@noom/noom-contracts/noom_contracts/billing/payment_method/payment_method";
import { BillingUserProto } from "@noom/noom-contracts/noom_contracts/billing/user/billing_user";
import { useMedCheckoutMethods } from "./medCheckout";
import { trackTask } from "src/utils/monitoring/events/tasks";
import {
  getInAppAutoCookie,
  getWebAuthCookie,
} from "src/utils/authCookieParser";
import { getSessionState } from "src/pageDefinitions/session";
import Cookies from "js-cookie";

async function getBillingUserUpdates(
  token: string,
  paymentSource: PaymentMethodProto_Source
) {
  const { accessCode } = getActiveUser();
  const { checkout } = getStore().getState();
  const billingName = shouldUseStripePaymentElement()
    ? getFullNameFromBillingAddress(checkout.billingAddress)
    : checkout.billingName;
  let activePaymentMethod: Partial<PaymentMethodProto> | undefined;
  if (token) {
    const billingAddress = await createAddress({
      accessCode,
      address: checkout.billingAddress,
      recipient: billingName,
    });
    activePaymentMethod = {
      token,
      source: paymentSource,
      billingAddressId: billingAddress.id,
    };
  }

  let homeAddressId: string | undefined;
  let preferredShippingAddressId: string | undefined;
  if (requiresPhysicalAddress()) {
    const physicalAddress = selectResolvedPhysicalAddressShortRegion(
      getStore().getState()
    );
    const homeAddress = await createAddress({
      accessCode,
      address: physicalAddress,
      recipient: physicalAddress.name ?? billingName,
    });
    homeAddressId = homeAddress.id;
    preferredShippingAddressId = isSelectedMedPlanWithMedication()
      ? homeAddressId
      : undefined;
  }
  return {
    activePaymentMethod,
    preferredShippingAddressId,
    homeAddressId,
  };
}

export function getAuthDebugInfo(authState?: ReturnType<typeof useAuth>) {
  // Temporary debug information to see the authentication state.
  // TODO: Remove this in UPSELL-1608
  try {
    return {
      activeUserIsSet: !!getActiveUser(),
      loginCallbackActiveUserSet:
        getSessionState("browser").loginCallbackActiveUserSet,
      loginCallbackAuthCookieDomain:
        getSessionState("browser").loginCallbackAuthCookieDomain,
      currentDomain: window.location.hostname,
      hasHiveControlCookie: !!Cookies.get("hive-control"),
      hasHiveAuthCookie: !!Cookies.get("hive-auth"),
      hasHiveAuthCopyCookie: !!Cookies.get("hive-auth-copy"),
      hasHiveAuth1DayCookie: !!Cookies.get("hive-auth-1day"),
      hasHiveAuthDomainCookie: !!Cookies.get("hive-auth-domain"),
      hasUserIdCookie: !!Cookies.get("_userId"),
      autologinDataCookieSet: !!getInAppAutoCookie(),
      identityDataCookieSet: !!getWebAuthCookie(),
      hasHiveSession: !!sessionStorage.getItem("hive-auth"),
      hasHiveAuthCopySession: !!sessionStorage.getItem("hive-auth-copy"),
      hasHiveLocalStorage: !!localStorage.getItem("hive-auth"),
      hasHiveAuthCopyLocalStorage: !!localStorage.getItem("hive-auth-copy"),

      ...(authState
        ? {
            isAuthenticated: authState.isAuthenticated,
            hasAuthState: !!authState.state,
            ssoSet: !!authState.state?.sso,
            coachServerSet: !!authState.state?.coachServer,
            shouldReLogin: authState.shouldReLogin(),
          }
        : {}),
    };
  } catch (err) {
    captureException(err);
    return {
      debugError: err,
    };
  }
}

export function useUpdateBillingUser() {
  const checkout = useCheckout();
  const dispatch = useAppDispatch();
  const { createOrderAndBillingCyclePreview } = useMedCheckoutMethods();
  const accessCode = getActiveUser()?.accessCode;
  const authState = useAuth();

  async function updateBillingUser(paymentToken?: string) {
    const previousProcessingState = getStore().getState().checkout.processing;
    let billingUser: BillingUserProto | undefined;
    const task = trackTask("UpdateBillingUser", {
      accessCode,
      ...getAuthDebugInfo(authState),
    });
    task.start();
    try {
      dispatch(
        updateCheckoutState({
          processing: {
            isProcessing: true,
            message: i18n.t("processing:updatePaymentInformation"),
          },
          purchaseErrorCode: "",
        })
      );
      const paymentSource =
        checkout.paymentType === paymentTypes.PAYPAL ? "BRAINTREE" : "STRIPE";
      const { activePaymentMethod, preferredShippingAddressId, homeAddressId } =
        await getBillingUserUpdates(paymentToken, paymentSource);
      billingUser = await updateBillingUserRequest({
        accessCode,
        activePaymentMethod,
        preferredShippingAddressId,
        homeAddressId,
      });
      dispatch(setBillingUser(billingUser));
      task.success();
    } catch (err) {
      captureException(err);
      task.failed(err);
      dispatch(
        updateCheckoutState({
          showPaymentInfoErrors: true,
        })
      );
      dispatch(updateCheckoutState({ processing: previousProcessingState }));
      throw new ValidationError([errorConstants.errorUpdateBillingUser]);
    }
    try {
      if (billingUser) {
        // Recreate the order only if the billing user was updated
        await createOrderAndBillingCyclePreview(false);
      }
    } catch (err) {
      captureException(err);
      throw new ValidationError([errorConstants.errorCreateOrder]);
    } finally {
      dispatch(updateCheckoutState({ processing: previousProcessingState }));
    }
  }

  return {
    updateBillingUser: useCallbackRef(updateBillingUser),
  };
}
